import Button from '@/components/Buttons/Button';
import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { BlackInformationChip } from '@/components/Teleprompter/BlackInformationChip';
import { Countdown } from '@/components/Teleprompter/Controls/Countdown';
import AltArrowLeft from '@/images/arrows/AltArrowLeft.svg';
import AltArrowRight from '@/images/arrows/AltArrowRight.svg';
import Record from '@/images/Record.svg';
import Restart from '@/images/Restart.svg';
import Stop from '@/images/Stop.svg';
import { css, keyframes, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import { IdleGuide } from './IdleGuide';

const Container = styled.div`
    height: 100%;
    width: 50vw;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const Overlay = styled.div`
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    backdrop-filter: blur(12px);
    background: rgba(0, 0, 0, 0.25);
`;

const PreviewInfo = styled.div`
    display: flex;
    align-items: center;
    max-width: 210px;

    color: white;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.4px;

    p {
        margin: 0 8px;
        min-width: 58px;
    }

    > svg {
        margin: 8px;
    }
`;

const IndicatorRecorder = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 22px;
    margin-top: 12vh;
`;

const Indicator = styled.div`
    position: absolute;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 24px;
`;

const IndicatorLine = styled.div`
    width: 100%;
    height: 1px;
    background-color: white;
`;

const StyledBlackInformationChip = styled(BlackInformationChip)`
    position: absolute;
    transform: translate(calc(-100% - 20px), 0);
`;

const scrollText = keyframes`
  from {
    transform: translateY(15vh);
  }
  to {
    transform: translateY(-100%);
  }
`;

const disappear = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const PrompterContainer = styled.div`
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
`;

const PrompterArea = styled.div<{ isStaticText?: boolean }>`
    height: 60vh; /* Set the height of the container */
    width: 100%;
    margin: 0 auto;
    color: white;
    box-sizing: border-box;
    position: relative;

    ${({ isStaticText }) =>
        !isStaticText &&
        `overflow: hidden;
         mask-image: linear-gradient(
            to bottom,
            rgba(0, 0, 0, 1) 0%,
            rgba(0, 0, 0, 1) 25%,
            rgba(0, 0, 0, 1) 50%,
            rgba(0, 0, 0, 0.5) 75%,
            rgba(0, 0, 0, 0) 100%
         )`};
`;

const PrompterText = styled.div`
    padding: 0 48px;
    display: inline-block;
    white-space: pre-wrap;
    position: absolute;

    font-size: 44px;
    font-weight: 700;
    line-height: 150%; /* 66px */
`;

const SafeZoneBorders = styled.div<{ isSafeZone: boolean }>`
    position: relative;
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;

    ${({ isSafeZone, theme }) =>
        isSafeZone &&
        css`
            &::before,
            &::after {
                content: '';
                position: absolute;
                top: 0;
                height: 100%;
                border-left: 4px dashed ${theme.colors.warn[500]};
            }

            &::before {
                left: 0;
            }

            &::after {
                right: 0;
            }
        `}
`;

const PrompterDynamicText = styled(PrompterText)<{ delay: number }>`
    width: 100%;
    animation: ${({ delay }) => css`
        ${scrollText} 0s linear ${delay + 1}s
    `};
    animation-fill-mode: forwards;
    transform: translateY(15vh);
`;

const PrompterStaticText = styled(PrompterText)`
    padding-top: 10rem;
    width: 100%;
    text-align: center;
`;

const PrompterDisappearingText = styled(PrompterText)<{ delay: number }>`
    margin-top: 10rem;
    margin-left: 50%;
    opacity: 0;
    animation: ${disappear} linear;
    animation-duration: 2s;
    animation-delay: ${({ delay }) => `${delay}s`};
    transform: translate(-50%);
`;

const PrompterAlwaysVisibleText = styled(PrompterText)`
    padding: 20px 48px;
    margin-top: 100px;
    margin-left: 50%;
    opacity: 1;
    transform: translate(-50%);
    width: 100%;
`;

const StyledButton = styled(Button)`
    margin-top: auto;
    gap: 4px;
    width: 216px;
`;

const convertSecondsToTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const getReadingTimeInSeconds = (text: string, wordsPerMinute = 120): number => {
    const words = text.split(/\s+/).length; // Split the text by whitespace to count words
    const readingTimeMinutes = words / wordsPerMinute;
    const readingTimeSeconds = readingTimeMinutes * 60;
    return Math.round(readingTimeSeconds);
};

interface Props {
    text?: string;
    startRecording: () => void;
    finishRecording: () => void;
    restartRecording: () => void;
    isRecording: boolean;
    minRecordingTime?: number;
    recordingTime?: number;
    countdownTime?: number;
    wordsPerMinute?: number;
    safeZoneToggle?: boolean;
    pauseRecording?: () => void;
    resumeRecording?: () => void;
    nextPageStaticText?: string;
    lastSectionTime?: number;
    isStaticText?: boolean;
}

export const RecordingLayer = ({
    text,
    startRecording,
    finishRecording,
    restartRecording,
    isRecording,
    wordsPerMinute,
    minRecordingTime = 15,
    recordingTime = 180,
    countdownTime = 5,
    safeZoneToggle = false,
    pauseRecording,
    resumeRecording,
    nextPageStaticText,
    lastSectionTime,
    isStaticText,
}: Props) => {
    const theme = useTheme();
    const dynamicTextRef = useRef<HTMLDivElement>(null);
    const intervalRef = useRef<NodeJS.Timeout>();

    const [_, setElapsedTime] = useState(0);
    const [minTimeReached, setMinTimeReached] = useState(false);
    const [timeString, setTimeString] = useState(convertSecondsToTime(0));
    const [isFinalSection, setIsFinalSection] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [isCountdownActive, setIsCountdownActive] = useState(true);

    const startRecordingCountdown = () => {
        const countdown = setInterval(() => {
            setElapsedTime(prevTime => {
                const newTime = prevTime + 1;
                setTimeString(convertSecondsToTime(newTime));

                if (newTime >= recordingTime) {
                    clearInterval(countdown);
                    finishRecording();
                    return 0;
                }

                if (lastSectionTime && newTime >= recordingTime - lastSectionTime && !isFinalSection) {
                    setIsFinalSection(true);
                    handlePause();
                }

                setMinTimeReached(newTime >= minRecordingTime);
                return newTime;
            });
        }, 1000);

        intervalRef.current = countdown;
    };

    const handleStart = () => {
        startRecordingCountdown();
        startRecording();
    };

    const handlePause = () => {
        setIsPaused(true);
        setIsCountdownActive(false);
        clearInterval(intervalRef.current);
        pauseRecording?.();
    };
    const handleResume = () => {
        setIsPaused(false);
        setIsCountdownActive(true);
    };

    useEffect(() => {
        return () => {
            clearInterval(intervalRef.current);
        };
    }, []);

    useEffect(() => {
        const textElement = dynamicTextRef.current;

        if (textElement && text) {
            const time = getReadingTimeInSeconds(text, wordsPerMinute);
            textElement.style.animationDuration = `${time}s`;
        }
    }, [text]);

    const handleCountdownEnd = () => {
        if (isFinalSection) {
            startRecordingCountdown();
            resumeRecording?.();
        } else {
            handleStart();
        }
    };

    return (
        <>
            {!!text && <Overlay />}
            <Container>
                {isCountdownActive && <Countdown countdownTime={countdownTime} onCountdownEnd={handleCountdownEnd} />}
                {isRecording && (
                    <IndicatorRecorder>
                        <StyledBlackInformationChip>
                            <PreviewInfo>
                                <MaskedIcon src={Record} size="24px" color={theme.colors.error[600]} />
                                <p>{timeString}</p>
                                <>{!isFinalSection ? 'Talking' : 'Silent'}</>
                                <IconButton onClick={restartRecording} ariaLabel="restart-recording">
                                    <MaskedIcon src={Restart} size="24px" color={theme.colors.white} />
                                </IconButton>
                            </PreviewInfo>
                        </StyledBlackInformationChip>

                        {!!text && !isStaticText && (
                            <Indicator>
                                <MaskedIcon src={AltArrowRight} size="24px" color={theme.colors.white} />
                                <IndicatorLine />
                                <MaskedIcon src={AltArrowLeft} size="24px" color={theme.colors.white} />
                            </Indicator>
                        )}
                    </IndicatorRecorder>
                )}

                <PrompterContainer>
                    <SafeZoneBorders isSafeZone={safeZoneToggle}>
                        <PrompterArea isStaticText={isStaticText}>
                            {isFinalSection ? (
                                <PrompterAlwaysVisibleText>{nextPageStaticText || 'Next Section'}</PrompterAlwaysVisibleText>
                            ) : text ? (
                                isStaticText ? (
                                    <PrompterStaticText>{text}</PrompterStaticText>
                                ) : (
                                    <PrompterDynamicText ref={dynamicTextRef} delay={countdownTime}>
                                        {text}
                                    </PrompterDynamicText>
                                )
                            ) : (
                                <PrompterDisappearingText delay={countdownTime}>Freestyle</PrompterDisappearingText>
                            )}
                        </PrompterArea>
                    </SafeZoneBorders>

                    {isPaused && <IdleGuide />}
                </PrompterContainer>

                {isPaused && (
                    <StyledButton color="secondary" onClick={handleResume}>
                        <MaskedIcon src={Record} size="24px" color={theme.colors.error[600]} />
                        Start recording
                    </StyledButton>
                )}

                {isRecording && minTimeReached && !isPaused && (
                    <StyledButton color="error" onClick={finishRecording}>
                        <MaskedIcon src={Stop} size="24px" color={theme.colors.white} />
                        Finish Recording
                    </StyledButton>
                )}
            </Container>
        </>
    );
};
